/*
Handles Task Management stuff (such as preventing editing when in read-only states), including the task-related buttons shown in the app bar.

TASK MANAGEMENT REQUIREMENTS:
- Tasks begin in ToDo state, and should be changed to InProgress as soon as the user begins annotating.
- Tasks should change to InReview when labeler submits his work.
- Tasks should change to Done when QA user (anyone is allowed to do QA) Approves.
- A Flag for Voices button should reject the task with a comment that human voices are present.
- An optional mode URL param should allow users to override the current task state. They can use "label" or "qa" modes in the URL (just like RAIL), which will override any state the task is currently in.

TASK MANAGEMENT CORNER CASES:
- UI should be locked (prevent editing of any kind) when task is in Done state. UI should also be locked when in QA state unless user pushes Enable Editing button.
- When you submit audio with no siren sounds, a confirm dialog appears.
- When you attempt to flag for rejection and there are annotations, the app prompts you to delete them first.
- Submit Task button should result in UI becoming locked (read-only), even if 'label' is the mode in the URL that overrides current task state.
- When a task that was already rejected is loaded with label or QA modes, a dialog should appear prompting the user to confirm they want to edit it.
- Tasks in Rejected state should not allow editing, even if loaded in qa / label modes, unless user specifically confirms they intend this.
- UI should clearly show locked state when user cannot edit. (For instance, when a task is Done, or when in QA mode.)
*/

import { FC, MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { Button, ConfigProvider, message, Modal, Tooltip, theme } from 'antd';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  audioExtractionTokenAtom,
  curAnnotationTypesAtom,
  userCanEditAtom,
} from 'packages/main/components/audio-tool/atoms';
import { useAudioTaskSWR } from 'packages/main/components/audio-tool/api';
import { AppBarActionsPortal } from 'packages/main/components/Layouts/portals';
import {
  changeTaskStateAPI,
  rejectTaskAPI,
  resetTask,
} from 'packages/lib/utils/legacy-tsapi/use-task-group-tasks';
import ButtonProg from 'packages/lib/common-ui/button-with-progress';
import { REQ_STATUS } from 'packages/lib/common-types/types';
import { isLocalhost } from 'packages/main/serviceWorkerRegistration';
import { css } from '@emotion/css';
import { countNumRegionsTotal } from 'packages/main/components/audio-tool/utils';
import { ExclamationCircleFilled, FlagOutlined } from '@ant-design/icons';
import { TaskModes } from 'packages/main/components/audio-tool/audio-tool-types';
import { useParams } from 'react-router-dom';
import type { Task } from 'packages/lib/api/annotations/contract/task';
import { taskStateZod } from 'packages/lib/api/annotations/contract/enums';

const { confirm } = Modal;

// For dev purposes, it can be useful to allow a task to be reset, when testing the Submit and Reject buttons, to revert that state.
// Even when true, this only enables this developer button on localhost.
const SHOW_RESET_TASK_BUTTON_ON_LOCALHOST = true;

// Disabled state default for Ant's Ghost button doesn't actually have a ghost background (wtf)
const antPrimaryOverBlack = css`
  &.ant-btn[disabled],
  &.ant-btn[disabled]:hover,
  &.ant-btn-primary[disabled],
  &.ant-btn-primary[disabled]:hover {
    background: #0b408a;
    color: gray;
    border-color: black;
  }
`;
/* Since it took some time to get this CSS right, keeping for now since this will be useful again somewhere else.
const antGhostDangerButton = css`
  &.ant-btn[disabled],
  &.ant-btn[disabled]:hover {
    background: transparent;
    color: #945354;
    border-color: #945354;
  }
`;*/

export const AudioAppBarTaskMgmt: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  // Currently we are using a hybrid of spreadsheets (managed by Jason Vu) to manage tasks, AND the task API. As such, the mode, which is determined
  // from the URL, overrides the task.state loaded from the API.
  const { mode: modeFromURL } = useParams<{ mode?: string }>();
  const [messageApi, contextHolder] = message.useMessage();

  const audioExtractionToken = useRecoilValue(audioExtractionTokenAtom);
  const [userEnabledEditing, setUserEnabledEditing] = useState(false); // In QA state, user can manually enable editing
  const [userOverrodeRejectedState, setUserOverrodeRejectedState] =
    useState(false);
  const setUserCanEdit = useSetRecoilState(userCanEditAtom);
  const { data: task, mutate: reloadTask } =
    useAudioTaskSWR(audioExtractionToken);
  // Since we don't require separate permissions for annotation vs  Q/A, don't allow pushing of both Submit and Approve back-to-back without at
  // least reloading the task. Since in theory a different person should do each.
  // Also used to prevent editing after submit, even if URL mode is in 'label' state, as this would allow editing even after submit.
  const [didSubmit, setDidSubmit] = useState(false);

  const [rejectButtonStatus, setRejectButtonStatus] = useState<REQ_STATUS>(
    REQ_STATUS.success
  );
  const [submitButtonStatus, setSubmitButtonStatus] = useState<REQ_STATUS>(
    REQ_STATUS.success
  );

  // URL mode can override the task state from the API
  // Special case: URL mode overrides 'rejected' state from API only if user confirms this (userOverrodeRejectedState === true)
  let taskState = task ? task.state : taskStateZod.enum.ToDo;
  const notRejected =
    taskState !== taskStateZod.enum.Rejected || userOverrodeRejectedState;
  if (modeFromURL === TaskModes.Label && notRejected)
    taskState = taskStateZod.enum.InProgress;
  if (modeFromURL === TaskModes.QA && notRejected)
    taskState = taskStateZod.enum.InReview;

  const taskIsInLabelingState =
    taskState === taskStateZod.enum.ToDo ||
    taskState === taskStateZod.enum.InProgress;

  // Corner Case: if a task has been rejected, but the user loads it with Label or QA modes in URL, Jason Vu would like for it
  // to still be editable, so a task doesn't get permanently stuck in Rejected with no easy way to recover. But do warn the user first.
  useEffect(() => {
    if (
      task &&
      !didSubmit &&
      task.state === taskStateZod.enum.Rejected &&
      (modeFromURL === TaskModes.Label || modeFromURL === TaskModes.QA)
    ) {
      confirm({
        title: 'Task was Rejected',
        icon: <ExclamationCircleFilled />,
        okText: 'Yes',
        okButtonProps: { danger: true },
        width: 460,
        content: (
          <span>
            This audio was previously rejected for this reason: <br />
            <br /> <b>{task.comments}</b>
            <br />
            <br /> Are you sure you want to override this and{' '}
            {modeFromURL === TaskModes.Label ? 'label' : 'QA'} it?
          </span>
        ),
        onOk() {
          setUserOverrodeRejectedState(true);
        },
      });
    }
  }, [modeFromURL, task, didSubmit]);

  // Change whether user can edit if a) Task is in labeling state, b) User clicked Enable Editing button, and c) User did NOT just submit a task
  useEffect(() => {
    setUserCanEdit((taskIsInLabelingState || userEnabledEditing) && !didSubmit);
  }, [taskIsInLabelingState, userEnabledEditing, didSubmit, setUserCanEdit]);

  // Developer-only feature (shown only on localhost)
  const handleResetClick = useCallback<MouseEventHandler<HTMLElement>>(
    async (e) => {
      if (task) {
        await resetTask(task?.token, true);
        void reloadTask();
        setDidSubmit(false);
      }
    },
    [task, reloadTask]
  );

  const handleEnableEditingClick = useCallback(() => {
    setUserEnabledEditing((curVal) => !curVal);
  }, []);

  const handleRejectClick = useRecoilCallback(
    ({ snapshot, set }) =>
      async (e) => {
        if (task) {
          const numRegions = await countNumRegionsTotal(snapshot);

          if (numRegions > 0) {
            Modal.warning({
              title: 'Audio has annotations',
              content:
                'To Flag for Voices, you must first delete all annotations. Do this, then try again.',
            });
          } else {
            setRejectButtonStatus(REQ_STATUS.pending);

            try {
              const [, resp] = await rejectTaskAPI(
                task?.token,
                'Human voices were present in the audio'
              );

              setRejectButtonStatus(
                resp.ok ? REQ_STATUS.success : REQ_STATUS.error
              );

              // When user rejects audio, we want to clear any annotation types (Wail, etc.) that are visible, so the Rejected message appears.
              set(curAnnotationTypesAtom, []);
              setDidSubmit(true);
              void reloadTask();

              if (!resp.ok) {
                void messageApi.open({
                  type: 'error',
                  content: 'Rejecting the task failed.',
                });
              }
            } catch (e) {
              setRejectButtonStatus(REQ_STATUS.error);
              void messageApi.open({
                type: 'error',
                content: 'Rejecting the task failed.',
              });
            }
          }
        }
      },
    [task, messageApi, reloadTask]
  );

  const finishSubmitOrApprove = useCallback(
    async (task: Task, action: 'submitLabeling' | 'approveQA') => {
      setSubmitButtonStatus(REQ_STATUS.pending);
      const actionVerb = action === 'submitLabeling' ? 'Submit' : 'Approve';

      try {
        const [, resp] = await changeTaskStateAPI(
          task.token,
          action === 'submitLabeling'
            ? taskStateZod.enum.InReview
            : taskStateZod.enum.Done
        );
        setDidSubmit(true);
        setSubmitButtonStatus(resp.ok ? REQ_STATUS.success : REQ_STATUS.error);
        void reloadTask();

        void messageApi.open({
          type: resp.ok ? 'success' : 'error',
          content: resp.ok
            ? `${actionVerb} successful!`
            : `${actionVerb} failed.`,
        });
      } catch (e) {
        setSubmitButtonStatus(REQ_STATUS.error);
        void messageApi.open({
          type: 'error',
          content: `${actionVerb} failed.`,
        });
      }
    },
    [messageApi, reloadTask]
  );

  const handleSubmitOrApproveClick = useRecoilCallback(
    ({ snapshot, set }) =>
      async (e, action: 'submitLabeling' | 'approveQA') => {
        if (task) {
          if (action === 'submitLabeling') {
            const numRegions = await countNumRegionsTotal(snapshot);

            if (numRegions === 0) {
              confirm({
                title: 'Submit with No Annotations?',
                icon: <ExclamationCircleFilled />,
                content:
                  'You are submitting the audio as containing No Siren Sounds. If this is correct, push OK.',
                onOk() {
                  void finishSubmitOrApprove(task, action);
                },
              });
            } else {
              // There were regions present, so no confirm necessary to submit
              void finishSubmitOrApprove(task, action);
            }
          } else {
            // Going from QA to Done, so no confirmation needed
            void finishSubmitOrApprove(task, action);
          }
        }
      },
    [task, finishSubmitOrApprove]
  );

  return (
    <>
      {contextHolder}
      <AppBarActionsPortal>
        <ConfigProvider
          theme={{
            algorithm: theme.darkAlgorithm,
          }}
        >
          {task && (
            <>
              {SHOW_RESET_TASK_BUTTON_ON_LOCALHOST && isLocalhost && (
                <Button
                  style={{ marginRight: '20px' }}
                  onClick={handleResetClick}
                >
                  Reset Task [Dev]
                </Button>
              )}
              {taskState === taskStateZod.enum.InReview && !didSubmit && (
                <Button
                  style={{ marginRight: '20px' }}
                  onClick={handleEnableEditingClick}
                >
                  {userEnabledEditing ? (
                    <span>Disable Editing</span>
                  ) : (
                    <span>Enable Editing</span>
                  )}
                </Button>
              )}
              <Tooltip title="Click here if human voices can be heard in the audio.">
                <ButtonProg
                  error={rejectButtonStatus === REQ_STATUS.error}
                  loading={rejectButtonStatus === REQ_STATUS.pending}
                  danger
                  type="primary"
                  icon={<FlagOutlined />}
                  onClick={handleRejectClick}
                  style={{ marginRight: '20px' }}
                  disabled={!task || task.state === taskStateZod.enum.Rejected}
                >
                  Flag for Voices
                </ButtonProg>
              </Tooltip>
              {taskIsInLabelingState && (
                <Tooltip title="Click here once all annotations have been completed.">
                  <ButtonProg
                    type="primary"
                    error={submitButtonStatus === REQ_STATUS.error}
                    loading={submitButtonStatus === REQ_STATUS.pending}
                    onClick={(e): Promise<void> =>
                      handleSubmitOrApproveClick(e, 'submitLabeling')
                    }
                    style={{ marginRight: '12px' }}
                    className={antPrimaryOverBlack}
                    disabled={didSubmit}
                  >
                    Submit Labeled Task
                  </ButtonProg>
                </Tooltip>
              )}
              {taskState === taskStateZod.enum.InReview && (
                <Tooltip title="Click here to approve Q/A for this task.">
                  <ButtonProg
                    type="primary"
                    error={submitButtonStatus === REQ_STATUS.error}
                    loading={submitButtonStatus === REQ_STATUS.pending}
                    onClick={(e): Promise<void> =>
                      handleSubmitOrApproveClick(e, 'approveQA')
                    }
                    style={{ marginRight: '12px' }}
                    className={antPrimaryOverBlack}
                    disabled={didSubmit}
                  >
                    Approve
                  </ButtonProg>
                </Tooltip>
              )}
            </>
          )}
        </ConfigProvider>
      </AppBarActionsPortal>
    </>
  );
};

export default AudioAppBarTaskMgmt;
